import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import RangeFilter from '@blaze-react/range-filter';
import { useRouter } from 'next/router';
import Select from '@blaze-react/select';
import { parseUrl } from 'query-string';
import { useGetSingleEntitySchema } from '../../../hooks';
import { getSelectOptions, getIntersectedProp, getRangeValue, calculateMinMax } from '../helpers';
import { decodeValue } from '../helpers/decode-encode';

const Range = ({
  rangeInterval,
  label,
  entity,
  propsToDisplay,
  dataAggregations,
  updateFilterValues,
  filterValues,
  shouldSearch,
  elementTitle,
  unit
}) => {
  const router = useRouter();
  const debounceAmount = shouldSearch ? 1200 : 400;
  const { query } = parseUrl(decodeValue(router.asPath));
  const hasMultipleOptions = propsToDisplay.length > 1;
  const initialRangeOption = getIntersectedProp(query, propsToDisplay) || propsToDisplay[0];
  const rangeOption =
    (filterValues[initialRangeOption] && filterValues[initialRangeOption].selectedOption) ||
    initialRangeOption;
  const rangeValue = filterValues[rangeOption];

  const { data: { getEntitySchema = {} } = {}, loading } = useGetSingleEntitySchema(
    entity,
    !hasMultipleOptions
  );

  if (loading || !rangeOption || !rangeValue) return '';

  const options = getSelectOptions(getEntitySchema, propsToDisplay);
  const { min, max } = calculateMinMax(dataAggregations[rangeOption], rangeInterval);
  const minValue = rangeValue.minValue < min ? min : rangeValue.minValue;
  const maxValue = rangeValue.maxValue > max ? max : rangeValue.maxValue;
  const valueToUse = { ...rangeValue, min, max, minValue, maxValue };

  const handleChange = debounce((option, value) => {
    updateFilterValues({ [option]: { ...value, selectedOption: rangeOption } }, shouldSearch);
  }, debounceAmount);

  let displayLabel = '';
  if (!hasMultipleOptions) {
    displayLabel = label;
  }

  return (
    <>
      {elementTitle && <div className="heading heading--section">{elementTitle}</div>}
      {hasMultipleOptions && (
        <>
          <label>{label}</label>
          <Select
            value={rangeOption}
            id={rangeOption}
            options={options}
            required
            onChange={({ value }) => {
              const updatedOptions = {};
              propsToDisplay.forEach(prop => {
                const { min: aggMin, max: aggMax } = calculateMinMax(
                  dataAggregations[prop],
                  rangeInterval
                );
                const initValue = getRangeValue(null, rangeInterval, aggMin, aggMax);
                updatedOptions[prop] = { ...initValue, selectedOption: value };
              });

              updateFilterValues(updatedOptions, false);
            }}
          />
        </>
      )}
      <div
        className={classNames('range-filter-wrapper', {
          'has-unit': unit
        })}
        style={unit ? { '--unit': `"${unit}"` } : {}}>
        <RangeFilter
          key={`${rangeOption}-${min}-${max}`}
          name={rangeOption}
          label={displayLabel}
          value={valueToUse}
          id={rangeOption}
          onChange={({ value }) => {
            handleChange(rangeOption, value);
          }}
        />
      </div>
    </>
  );
};

Range.propTypes = {
  propsToDisplay: PropTypes.array.isRequired,
  entity: PropTypes.string.isRequired,
  filterValues: PropTypes.object.isRequired,
  updateFilterValues: PropTypes.func.isRequired,
  shouldSearch: PropTypes.bool,
  rangeInterval: PropTypes.number,
  label: PropTypes.string,
  elementTitle: PropTypes.string,
  dataAggregations: PropTypes.object.isRequired,
  unit: PropTypes.string
};

Range.defaultProps = {
  shouldSearch: false,
  rangeInterval: 1,
  label: '',
  elementTitle: '',
  unit: ''
};

export default Range;
